import { toNumber } from './dioptre.js';
/**
 * Choosing a lens for a prescription. §6.4 `CAT-011`, §6.6 `DSP-004`, Appendix I.
 *
 * Release gate 3 is measured here: "A dispense with a power outside the lens range is blocked **at
 * the point of choice, not at submit**."
 *
 * ---
 *
 * **"At the point of choice" is the whole requirement, and it is a shape rather than a message.**
 *
 * `DSP-004`: "Warnings are surfaced inline at the point of choice, **not at submit**." §15's flow
 * says the same thing from the other end: "As each choice is made the system re-evaluates
 * compatibility, availability range, corridor vs frame B measurement, and cut-out. Failures surface
 * at the point of choice, **with the reason and the nearest workable alternative**."
 *
 * A validator that runs on submit can only say no to work already done. This one answers a different
 * question: **given this prescription, which lenses can be chosen at all**, so the choice a
 * dispenser is offered is already the set that works. `evaluateChoice` then answers for one product,
 * and both share the same rule so a lens cannot pass the list and fail the check.
 *
 * The distinction matters commercially, not only ergonomically. A dispenser who picks a lens, takes
 * measurements, quotes a price and is then told the power is unavailable has to go back to the
 * customer and start again, which is the moment a sale is lost.
 *
 * ---
 *
 * **`RX-05` and this are not in conflict, and the difference is the point.** The Rx grid warns and
 * never blocks, because at Rx capture no lens has been chosen and the rule has nothing to evaluate
 * against. Here a product exists, so the same fact becomes a block. `decisions/0003` settled that,
 * and `RX-004`'s placement of the availability rule in the Rx capture section is recorded there as
 * a placement error rather than a second decision.
 */
export class DispenseError extends Error {
}
const tenths = (mm) => Math.round(mm * 10);
/**
 * The smallest blank that cuts to this frame at this PD, in millimetres.
 *
 * Assumption, because the spec gives no formula (the glossary says only "whether a blank is large
 * enough to cut"): `ED + 2 * decentration + edging allowance`, where decentration is the distance
 * from the optical centre (at the monocular PD) to the geometric centre of the lens shape
 * (`(A + DBL) / 2` from the nose). ED is the longest chord of the shape, so this is conservative and
 * can warn on a lens that would just cut. Computed in tenths of a millimetre so comparisons are exact.
 */
export function requiredBlankDiameterMm(i) {
    const centreTenths = (tenths(i.frameEyeSizeMm) + tenths(i.frameBridgeMm)) / 2;
    const decentration = Math.abs(centreTenths - tenths(i.monocularPdMm));
    return (tenths(i.frameEdMm) + 2 * decentration + tenths(i.edgingAllowanceMm)) / 10;
}
/** Whether the blank is at least the required diameter. Compared in tenths, never as raw floats. */
export function cutsOut(i) {
    return tenths(i.blankDiameterMm) >= tenths(requiredBlankDiameterMm(i));
}
/**
 * The `rx_type` values that require a lens carrying an add range.
 *
 * Settled by a product decision rather than derived, because the spec does not enumerate it:
 * `CAT-011` says only "add range for multifocals" and `RX_TYPES` has seven values.
 * `bifocal` and `progressive` are the two that are unambiguously multifocal. `near` is a
 * single-vision reading prescription with no add at all, `distance` and `safety` need none, and
 * `occupational` is a degressive lens in some catalogues and a plain reading lens in others, so
 * requiring an add range for it would refuse legitimate stock.
 *
 * This replaces inferring the answer from whether an add value happens to be present, which
 * `RxCapture.tsx` had already rejected for the same question: "§5.2 makes the prescription type a
 * header field, and the grid does not carry it... it is a visible choice rather than a guess
 * derived from whether an add happens to be filled in." The old inference also meant a dispenser
 * who typed 0.00 rather than leaving the add blank could not be sold a single-vision lens.
 */
export const RX_TYPES_REQUIRING_ADD_RANGE = ['bifocal', 'progressive'];
/**
 * Can this lens be dispensed for this prescription.
 *
 * Evaluated per eye, because a pair where one eye is in range and the other is not cannot be made
 * from this lens, and saying "out of range" without saying which eye sends a dispenser back to the
 * grid to work it out.
 */
export function evaluateChoice(product, eyes, options) {
    const findings = [];
    const singleVisionLens = product.range.addMin === undefined || product.range.addMax === undefined;
    /**
     * Whether this lens can carry the prescription's near addition at all.
     *
     * Stated once for the pair rather than once per eye, because it is a property of the product and
     * the prescription type and not of an eye: a progressive prescription cannot be made from a
     * single-vision lens, and saying so twice says nothing more. `TREATMENT_INCOMPATIBLE` below is
     * outside the eye loop for the same reason.
     *
     * The type decides, not whether a number was typed. A bifocal or progressive prescription is
     * refused even with the add row left blank, and a distance prescription carrying a recorded 0.00
     * add is not multifocal, which is what the old inference got wrong.
     */
    if (RX_TYPES_REQUIRING_ADD_RANGE.includes(options.rxType) && singleVisionLens) {
        findings.push({
            severity: 'block',
            code: 'LENS_NOT_MULTIFOCAL',
            message: `${product.name} is a single-vision lens and this is a ${options.rxType} prescription.`,
        });
    }
    for (const [eye, powers] of [
        ['od', eyes.od],
        ['os', eyes.os],
    ]) {
        const sphere = toNumber(powers.sphere);
        const cylinder = toNumber(powers.cylinder);
        const combined = Math.abs(sphere) + Math.abs(cylinder);
        if (sphere < product.range.sphereMin || sphere > product.range.sphereMax) {
            findings.push({
                severity: 'block',
                code: 'LENS_OUT_OF_RANGE',
                eye,
                message: `${product.name} is made from ${product.range.sphereMin.toFixed(2)} to ${product.range.sphereMax.toFixed(2)} sphere. The ${eye === 'od' ? 'right' : 'left'} eye is ${sphere.toFixed(2)}.`,
            });
        }
        if (Math.abs(cylinder) > product.range.cylinderMaxMagnitude) {
            findings.push({
                severity: 'block',
                code: 'LENS_OUT_OF_RANGE',
                eye,
                message: `${product.name} takes up to ${product.range.cylinderMaxMagnitude.toFixed(2)} cylinder. The ${eye === 'od' ? 'right' : 'left'} eye is ${cylinder.toFixed(2)}.`,
            });
        }
        if (product.range.combinedMax !== undefined && combined > product.range.combinedMax) {
            // The limit that catches a prescription both of whose individual powers are inside range.
            // A -6.00/-4.00 is inside a -8 sphere and a 4.00 cylinder limit and is 10.00 combined, which
            // is more glass than the blank has.
            findings.push({
                severity: 'block',
                code: 'LENS_OUT_OF_RANGE',
                eye,
                message: `${product.name} has a combined power limit of ${product.range.combinedMax.toFixed(2)}. The ${eye === 'od' ? 'right' : 'left'} eye is ${combined.toFixed(2)} combined.`,
            });
        }
        if (powers.add !== undefined && !singleVisionLens) {
            const add = toNumber(powers.add);
            if (add < product.range.addMin || add > product.range.addMax) {
                findings.push({
                    severity: 'block',
                    code: 'LENS_OUT_OF_RANGE',
                    eye,
                    message: `${product.name} takes an add from ${product.range.addMin.toFixed(2)} to ${product.range.addMax.toFixed(2)}. This one is ${add.toFixed(2)}.`,
                });
            }
        }
    }
    // CAT-013: an incompatible treatment is a block, not a warning. The lab cannot apply it.
    for (const treatmentId of options.treatmentIds ?? []) {
        if (!product.compatibleTreatmentIds.includes(treatmentId)) {
            findings.push({
                severity: 'block',
                code: 'TREATMENT_INCOMPATIBLE',
                message: `${treatmentId} cannot be applied to ${product.name}.`,
            });
        }
    }
    // CAT-014: a progressive below its minimum fitting height will not work in that frame. DSP-004
    // lists "insufficient fitting height for the chosen progressive" among the point-of-choice
    // failures, and it is a block: the lens physically cannot be glazed to that depth.
    if (product.minimumFittingHeightMm !== undefined &&
        options.fittingHeightMm !== undefined &&
        options.fittingHeightMm < product.minimumFittingHeightMm) {
        findings.push({
            severity: 'block',
            code: 'FITTING_HEIGHT_INSUFFICIENT',
            message: `${product.name} needs at least ${String(product.minimumFittingHeightMm)}mm of fitting height and this frame gives ${String(options.fittingHeightMm)}mm.`,
        });
    }
    if (options.cutOut !== undefined) {
        const c = options.cutOut;
        const blank = c.blankDiameterMm ??
            (product.diametersMm !== undefined && product.diametersMm.length > 0
                ? Math.max(...product.diametersMm)
                : undefined);
        if (c.edMm === undefined ||
            c.monocularPdMm === undefined ||
            c.eyeSizeMm === undefined ||
            c.bridgeMm === undefined ||
            blank === undefined) {
            findings.push({
                severity: 'warn',
                code: 'CUTOUT_NOT_CHECKED',
                message: `Cut-out was not checked for ${product.name}: it needs the frame eye size, bridge and ED, the monocular PD and a blank diameter.`,
            });
        }
        else {
            const input = {
                blankDiameterMm: blank,
                frameEyeSizeMm: c.eyeSizeMm,
                frameBridgeMm: c.bridgeMm,
                frameEdMm: c.edMm,
                monocularPdMm: c.monocularPdMm,
                edgingAllowanceMm: c.edgingAllowanceMm,
            };
            if (!cutsOut(input)) {
                const required = requiredBlankDiameterMm(input);
                const larger = (product.diametersMm ?? [])
                    .filter((d) => tenths(d) >= tenths(required))
                    .sort((a, b) => a - b)[0];
                findings.push({
                    severity: c.severity,
                    code: 'LENS_NO_CUTOUT',
                    message: "This lens won't cut to that frame at this PD. Try a larger diameter or a different frame." +
                        ` It needs at least ${String(required)} mm blank` +
                        (larger === undefined
                            ? '.'
                            : `; the nearest available diameter that cuts is ${String(larger)} mm.`),
                });
            }
        }
    }
    return {
        productId: product.id,
        canDispense: !findings.some((f) => f.severity === 'block'),
        findings,
    };
}
/**
 * The point-of-choice question: which of these lenses can be chosen at all.
 *
 * This is what makes gate 3 a shape rather than a message. A dispenser is offered the set that
 * works, so the out-of-range case is not something they can walk into and be told about later.
 */
export function dispensableProducts(products, eyes, options) {
    const verdicts = products.map((p) => evaluateChoice(p, eyes, options));
    return {
        available: verdicts.filter((v) => v.canDispense),
        unavailable: verdicts.filter((v) => !v.canDispense),
    };
}
/**
 * §15's "the nearest workable alternative", which the flow requires alongside the reason.
 *
 * A refusal with no alternative sends a dispenser back to a catalogue to search by hand, in front of
 * a customer. Ordered by how much range is left over, so the suggestion is a lens that fits rather
 * than the most expensive one that happens to.
 */
export function nearestAlternatives(products, eyes, options, limit = 3) {
    const { available } = dispensableProducts(products, eyes, options);
    const byId = new Map(products.map((p) => [p.id, p]));
    const strongest = Math.max(Math.abs(toNumber(eyes.od.sphere)) + Math.abs(toNumber(eyes.od.cylinder)), Math.abs(toNumber(eyes.os.sphere)) + Math.abs(toNumber(eyes.os.cylinder)));
    return available
        .map((v) => byId.get(v.productId))
        .sort((a, b) => headroom(a, strongest) - headroom(b, strongest))
        .slice(0, limit);
}
/** How much of the lens's range is unused by this prescription. Smaller is a closer fit. */
function headroom(product, strongestCombined) {
    const limit = product.range.combinedMax ??
        Math.max(Math.abs(product.range.sphereMin), Math.abs(product.range.sphereMax)) +
            product.range.cylinderMaxMagnitude;
    return limit - strongestCombined;
}
/**
 * `DSP-004`'s submit-time backstop.
 *
 * Gate 3 is about the point of choice and this is not a replacement for it: it is the guarantee that
 * the point-of-choice check cannot be bypassed by a client that never ran it. `RX-05` warns at Rx
 * capture and a write queue posts JSON, so the server checks the same rule again.
 */
export function assertDispensable(verdict) {
    const blocks = verdict.findings.filter((f) => f.severity === 'block');
    // Both, because either one alone is a gate that can be walked around. `canDispense` used to be
    // the only thing read, and this function takes a `ChoiceVerdict` as an argument rather than
    // computing one: a verdict carrying `canDispense: true` next to a block finding passed, which is
    // the shape a client that serialised its own verdict produces. Release gate 3 blocks an
    // out-of-range power "at the point of choice", and this is the server's half of that.
    if (verdict.canDispense && blocks.length === 0)
        return;
    const reason = blocks.length > 0
        ? blocks.map((b) => b.message).join(' ')
        : // `canDispense: false` with nothing blocking is a verdict `evaluateChoice` cannot produce,
            // so it is a verdict from somewhere else. Refused, and said so, rather than refused with an
            // empty reason after the colon.
            'the verdict refuses it and records no blocking finding, so it did not come from evaluateChoice.';
    throw new DispenseError(`${verdict.productId} cannot be dispensed for this prescription: ${reason}`);
}
//# sourceMappingURL=dispense.js.map